/**
 * LeftShield Portal API Service
 * Customer-facing API client for runtime enforcement and security management
 */

import type {
  ComplianceDashboard,
  ComplianceFramework,
  ComplianceControl,
  ControlDetail,
  ControlListResult,
  ControlMapping,
  FrameworkCoverage,
  Finding,
  FindingFilter,
  Vulnerability,
  VulnerabilityFilter,
  Run,
  ScanResult,
  ApiKey,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  ChatSession,
  ChatMessage,
  SendMessageRequest,
  Notification,
  SecurityDashboard,
  PaginatedResponse,
  IntegrationsSummary,
  ActivityLogEntry,
  ActivityType,
  DiagnosticSummary,
  DiagnosticTrend,
  DiagnosticSchedule,
  DiagnosticExportFormat,
  RuntimeBlindSpotsSummary,
  RuntimeBlindSpot,
  DiagnosticReportResponse,
  QualificationStatus,
  EnforcementOutcomes,
  DashboardOverview,
  MergeGatePolicy,
  UpdateMergeGatePolicyRequest,
  WebhookRegistration,
  PendingSuppression,
  SuppressionAuditEntry,
  NotificationPreference,
  NotificationChannel,
  NotificationDelivery,
  UsageSummary,
  UsageHistory,
  SubscriptionInfo,
  Invoice,
  RateLimitStatus,
  ReportType,
  GenerateReportRequest,
  ScoreSummary,
  EvaluationSummary,
  Agent,
  AgentDetail,
  AgentPagedResult,
  AgentListFilter,
  AgentRisk,
  ShadowAiSweep,
  UpdateAgentRequest,
  AgentRegistrationInput,
  McpServer,
  McpServerDetail,
  McpServerPagedResult,
  McpServerListFilter,
  McpServerRegistrationInput,
  McpTool,
  McpToolPagedResult,
  McpToolListFilter,
  McpToolRegistrationInput,
  McpRiskAssessment,
  UnapprovedMcpUsage,
  ResponsePolicy,
  ResponsePolicyPagedResult,
  ResponseAction,
  ResponseActionPagedResult,
  ResponseActionStatus,
  ResponseActionType,
  ResponseProposalPagedResult,
  CreateResponsePolicyInput,
  RegeVerdict,
  ExecutionDecision,
  ExecutionDecisionPagedResult,
  TrustScore,
  DriftCategory,
  DriftStatus,
  DriftSignalPagedResult,
  DriftIdentityState,
  DriftRecomputeSummary,
  DriftBaseline,
  SimulationResult,
  SimulationRunPagedResult,
  SimulateExecutionInput,
  GovernanceScore,
  ScoreDecomposition,
  WeightProfile,
  UpdateWeightProfileRequest,
  Glossary,
} from '../types';
import { msalInstance, apiRequest as msalApiRequest } from '../features/authentication';

import { getTenantIdentifier } from './tenant/tenantAccessor';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://localhost:7001/api';

// =============================================================================
// AUTHENTICATION HELPER
// =============================================================================

/**
 * Get authentication token from MSAL
 */
export async function getAccessToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return '';
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...msalApiRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    console.warn('Silent token acquisition failed, redirecting to login:', error);
    // Any failure to silently acquire a token (expired, revoked, network error)
    // should trigger a redirect to re-authenticate
    try {
      await msalInstance.acquireTokenRedirect(msalApiRequest);
    } catch (redirectError) {
      console.error('Token redirect failed:', redirectError);
    }
    return '';
  }
}

// =============================================================================
// HTTP CLIENT
// =============================================================================

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = await getAccessToken();
  const tenantId = getTenantIdentifier();
  const correlationId = crypto.randomUUID();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Correlation-Id': correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-Id': tenantId }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));

    // Handle subscription gate 403
    if (response.status === 403 && error.error === 'NO_ACTIVE_SUBSCRIPTION') {
      const subscriptionError = new Error(error.message || 'No active subscription') as Error & { code: string };
      subscriptionError.code = 'NO_ACTIVE_SUBSCRIPTION';
      throw subscriptionError;
    }

    // Some controllers (e.g. AgentsController) serialize the whole `Result<T>` envelope on
    // failure, which — absent a camelCase JSON contract resolver — comes back PascalCased
    // (`Message`, `Errors`). Read both casings defensively so field-level error mapping (B-03)
    // works regardless of which naming convention a given endpoint happens to use today.
    const apiError = new Error(error.message || error.Message || `HTTP ${response.status}`) as Error & {
      status: number;
      details?: Record<string, string[] | string>;
    };
    apiError.status = response.status;
    apiError.details = error.errors || error.Errors || error.validationErrors || error.ValidationErrors;
    throw apiError;
  }

  return response.json();
}

// =============================================================================
// COMPLIANCE API
// =============================================================================

export const complianceApi = {
  /**
   * Get compliance dashboard data
   */
  async getDashboard(): Promise<ComplianceDashboard> {
    return fetchWithAuth('/portal/dashboard/compliance');
  },

  /**
   * Get all compliance frameworks
   */
  async getFrameworks(): Promise<ComplianceFramework[]> {
    return fetchWithAuth('/portal/mcp/frameworks');
  },

  /**
   * Get framework details by code
   */
  async getFramework(code: string): Promise<ComplianceFramework> {
    return fetchWithAuth(`/portal/mcp/frameworks/${code}`);
  },

  /**
   * Get framework details by public GUID (§12746 GUID-routed detail page).
   * `GET /api/portal/mcp/frameworks/{referenceId:guid}` — a sibling route to
   * `getFramework`'s by-code lookup, disambiguated by ASP.NET's `:guid` route
   * constraint so both can share the same `frameworks/{value}` path shape.
   */
  async getFrameworkByReferenceId(referenceId: string): Promise<ComplianceFramework> {
    return fetchWithAuth(`/portal/mcp/frameworks/${referenceId}`);
  },

  /**
   * Get controls for a framework
   */
  async getControls(frameworkCode: string): Promise<ComplianceControl[]> {
    const result = await fetchWithAuth<{ controls: ComplianceControl[] }>(`/portal/mcp/controls/${frameworkCode}`);
    return result.controls;
  },

  /**
   * Get the rich control detail (guidance + mappings) by public GUID reference id —
   * §12772: the portal-reachable sibling to the admin-only catalog endpoint noted on
   * `ControlDetail.referenceId`, completing the last Feature #12746 drawer→route
   * conversion (`ControlCoveragePage`'s inline `ControlDetailDrawer` → `ControlDetailPage`).
   */
  async getControlDetailByReferenceId(referenceId: string): Promise<ControlDetail> {
    return fetchWithAuth(`/portal/mcp/controls/detail/${referenceId}`);
  },

  /**
   * Enable or disable a framework
   */
  async setFrameworkEnabled(frameworkId: string | number, enabled: boolean): Promise<void> {
    const action = enabled ? 'enable' : 'disable';
    return fetchWithAuth(`/portal/mcp/frameworks/${frameworkId}/${action}`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// SECURITY FINDINGS API
// =============================================================================

export const findingsApi = {
  /**
   * Get findings with filtering and pagination
   */
  async getAll(
    filter: FindingFilter = {},
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<Finding>> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());
    if (filter.status) params.set('status', filter.status);
    if (filter.severity) params.set('severity', filter.severity);
    if (filter.type) params.set('type', filter.type);
    if (filter.frameworkCode) params.set('frameworkCode', filter.frameworkCode);
    if (filter.assignedTo) params.set('assignedTo', filter.assignedTo);
    if (filter.repository) params.set('repository', filter.repository);
    return fetchWithAuth(`/portal/findings?${params}`);
  },

  /**
   * Get finding by ID
   */
  async getById(id: string): Promise<Finding> {
    return fetchWithAuth(`/portal/findings/${id}`);
  },

  /**
   * Get finding by public GUID (§12746 GUID-routed detail page).
   * `GET /api/portal/findings/{referenceId:guid}` — a sibling route to `getById`'s
   * `{id:int}` route, disambiguated by ASP.NET's `:guid` constraint so both share
   * the same `findings/{value}` path shape.
   */
  async getByReferenceId(referenceId: string): Promise<Finding> {
    return fetchWithAuth(`/portal/findings/${referenceId}`);
  },

  /**
   * Update finding status
   */
  async updateStatus(id: string, status: string, notes?: string): Promise<Finding> {
    return fetchWithAuth(`/portal/findings/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  },

  /**
   * Suppress a finding
   */
  async suppress(
    id: string,
    reason: string,
    justification: string,
    expiresAt?: string
  ): Promise<Finding> {
    return fetchWithAuth(`/portal/findings/${id}/suppress`, {
      method: 'POST',
      body: JSON.stringify({ reason, justification, expiresAt }),
    });
  },

  /**
   * Assign finding to a user
   */
  async assign(id: string, userId: string, dueDate?: string): Promise<Finding> {
    return fetchWithAuth(`/portal/findings/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId, dueDate }),
    });
  },
};

// =============================================================================
// VULNERABILITIES API
// =============================================================================

export const vulnerabilitiesApi = {
  /**
   * Get vulnerabilities with filtering and pagination
   */
  async getAll(
    filter: VulnerabilityFilter = {},
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<Vulnerability>> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());
    if (filter.status) params.set('status', filter.status);
    if (filter.severity) params.set('severity', filter.severity);
    if (filter.isKnownExploited !== undefined) params.set('isKnownExploited', filter.isKnownExploited.toString());
    if (filter.source) params.set('source', filter.source);
    return fetchWithAuth(`/portal/vulnerabilities?${params}`);
  },

  /**
   * Get vulnerability by ID
   */
  async getById(id: string): Promise<Vulnerability> {
    return fetchWithAuth(`/portal/vulnerabilities/${id}`);
  },

  /**
   * Get vulnerability by public GUID (§12746 GUID-routed detail page).
   * `GET /api/portal/vulnerabilities/{referenceId:guid}` — a sibling route to
   * `getById`'s `{id:int}` route, disambiguated by ASP.NET's `:guid` constraint so
   * both share the same `vulnerabilities/{value}` path shape.
   */
  async getByReferenceId(referenceId: string): Promise<Vulnerability> {
    return fetchWithAuth(`/portal/vulnerabilities/${referenceId}`);
  },

  /**
   * Acknowledge a vulnerability
   */
  async acknowledge(id: string, notes: string): Promise<Vulnerability> {
    return fetchWithAuth(`/portal/vulnerabilities/${id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },
};

// =============================================================================
// RUNS & SCANS API
// =============================================================================

export const runsApi = {
  /**
   * Get recent runs
   */
  async getRecent(limit = 10): Promise<Run[]> {
    return fetchWithAuth(`/portal/runs?limit=${limit}`);
  },

  /**
   * Get run by ID
   */
  async getById(id: string): Promise<Run> {
    return fetchWithAuth(`/portal/runs/${id}`);
  },

  /**
   * Get run by public GUID (§12746 GUID-routed detail page).
   * `GET /api/portal/runs/{referenceId:guid}` — a sibling route to `getById`'s
   * `{id:int}` route, disambiguated by ASP.NET's `:guid` constraint so both share
   * the same `runs/{value}` path shape.
   */
  async getByReferenceId(referenceId: string): Promise<Run> {
    return fetchWithAuth(`/portal/runs/${referenceId}`);
  },

  /**
   * Get scan results for a run
   */
  async getScanResults(runId: string): Promise<ScanResult[]> {
    return fetchWithAuth(`/portal/runs/${runId}/scans`);
  },

  /**
   * Trigger a new run
   */
  async trigger(request: {
    targetType: string;
    targetIdentifier: string;
    targetBranch?: string;
    frameworkCode: string;
    scanners: string[];
  }): Promise<Run> {
    return fetchWithAuth('/portal/runs', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Cancel a running scan
   */
  async cancel(id: string): Promise<void> {
    return fetchWithAuth(`/portal/runs/${id}/cancel`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// API KEYS API
// =============================================================================

export const apiKeysApi = {
  /**
   * Get all API keys
   */
  async getAll(includeRevoked = false): Promise<ApiKey[]> {
    const params = new URLSearchParams();
    if (includeRevoked) params.set('includeRevoked', 'true');
    return fetchWithAuth(`/portal/api-keys?${params}`);
  },

  /**
   * Create a new API key
   */
  async create(request: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    return fetchWithAuth('/portal/api-keys', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Revoke an API key
   */
  async revoke(id: string, reason?: string): Promise<void> {
    return fetchWithAuth(`/portal/api-keys/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Rotate an API key
   */
  async rotate(id: string): Promise<CreateApiKeyResponse> {
    return fetchWithAuth(`/portal/api-keys/${id}/rotate`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// AI NARRATOR API
// =============================================================================

/** Response shape from POST /narrator/message */
interface ChatResponseDto {
  sessionId: number;
  messageId: number;
  content: string;
  citations: Array<{ title: string; type: string; url?: string; snippet?: string }>;
  promptTokens: number;
  completionTokens: number;
}

/** Response shape from GET /narrator/sessions (paginated) */
interface ChatSessionListResult {
  sessions: Array<{
    id: number;
    title: string;
    status: string;
    messageCount: number;
    createdAt: string;
    lastMessageAt?: string;
  }>;
  totalCount: number;
  page: number;
  pageSize: number;
}

/** Response shape from GET /narrator/sessions/{id} */
interface ChatSessionDetailDto {
  id: number;
  title: string;
  status: string;
  context?: { type: string; currentPage?: string };
  messages: Array<{
    id: number;
    role: string;
    content: string;
    citations: Array<{ title: string; type: string; url?: string; snippet?: string }>;
    rating?: string;
    feedback?: string;
    createdAt: string;
  }>;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  createdAt: string;
  lastMessageAt?: string;
}

export const narratorApi = {
  /**
   * Get chat sessions — maps paginated API response to flat ChatSession[]
   */
  async getSessions(): Promise<ChatSession[]> {
    const result: ChatSessionListResult = await fetchWithAuth('/portal/narrator/sessions');
    return (result.sessions ?? []).map((s) => ({
      id: String(s.id),
      title: s.title,
      status: s.status as ChatSession['status'],
      contextType: 'General' as ChatSession['contextType'],
      totalPromptTokens: 0,
      totalCompletionTokens: 0,
      createdAt: s.createdAt,
      lastMessageAt: s.lastMessageAt,
      messages: [],
    }));
  },

  /**
   * Get chat session by ID — maps API detail DTO to frontend ChatSession
   */
  async getSession(id: string): Promise<ChatSession> {
    const result: ChatSessionDetailDto = await fetchWithAuth(`/portal/narrator/sessions/${id}`);
    return {
      id: String(result.id),
      title: result.title,
      status: result.status as ChatSession['status'],
      contextType: (result.context?.type ?? 'General') as ChatSession['contextType'],
      totalPromptTokens: result.totalPromptTokens,
      totalCompletionTokens: result.totalCompletionTokens,
      createdAt: result.createdAt,
      lastMessageAt: result.lastMessageAt,
      messages: (result.messages ?? []).map((m) => ({
        role: m.role as ChatMessage['role'],
        content: m.content,
        citationsJson: m.citations?.length ? JSON.stringify(m.citations) : undefined,
        rating: m.rating as ChatMessage['rating'],
        feedback: m.feedback,
        createdAt: m.createdAt,
      })),
    };
  },

  /**
   * Send a message — maps frontend request shape to API command shape
   */
  async sendMessage(request: SendMessageRequest): Promise<{ sessionId: string }> {
    // Map flat frontend request to nested API command shape
    const apiPayload = {
      sessionId: request.sessionId ? Number(request.sessionId) : null,
      message: request.message,
      context: request.contextType
        ? { type: request.contextType, currentPage: request.currentPage }
        : null,
    };
    const result: ChatResponseDto = await fetchWithAuth('/portal/narrator/message', {
      method: 'POST',
      body: JSON.stringify(apiPayload),
    });
    return { sessionId: String(result.sessionId) };
  },

  /**
   * Send a message and stream the response token-by-token via SSE.
   */
  async sendMessageStream(
    request: SendMessageRequest,
    callbacks: {
      onSessionId: (sessionId: string) => void;
      onToken: (text: string) => void;
      onDone: (meta: { sessionId: string; messageId: number; inputTokens: number; outputTokens: number }) => void;
      onError: (error: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const url = `${API_BASE_URL}/portal/narrator/message/stream`;
    const token = await getAccessToken();
    const tenantId = getTenantIdentifier();

    const apiPayload = {
      sessionId: request.sessionId ? Number(request.sessionId) : null,
      message: request.message,
      context: request.contextType
        ? { type: request.contextType, currentPage: request.currentPage }
        : null,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-Id': crypto.randomUUID(),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-Id': tenantId }),
      },
      body: JSON.stringify(apiPayload),
      signal,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      callbacks.onError(err.message || `HTTP ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError('Streaming not supported');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          const data = line.slice(6);
          switch (currentEvent) {
            case 'session': {
              const parsed = JSON.parse(data);
              callbacks.onSessionId(String(parsed.sessionId));
              break;
            }
            case 'token':
              callbacks.onToken(data);
              break;
            case 'done': {
              const parsed = JSON.parse(data);
              callbacks.onDone({
                sessionId: String(parsed.sessionId),
                messageId: parsed.messageId,
                inputTokens: parsed.inputTokens,
                outputTokens: parsed.outputTokens,
              });
              break;
            }
            case 'error': {
              const parsed = JSON.parse(data);
              callbacks.onError(parsed.error || 'Unknown error');
              break;
            }
          }
          currentEvent = '';
        }
      }
    }
  },

  /**
   * Rate a response
   */
  async rateResponse(sessionId: string, rating: string, feedback?: string): Promise<void> {
    return fetchWithAuth(`/portal/narrator/sessions/${sessionId}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating, feedback }),
    });
  },

  /**
   * Archive a session
   */
  async archiveSession(id: string): Promise<void> {
    return fetchWithAuth(`/portal/narrator/sessions/${id}/archive`, {
      method: 'POST',
    });
  },

  /**
   * Get a one-shot contextual AI summary (no session state)
   */
  async getSummary(contextType: string, contextData?: Record<string, unknown>): Promise<{ summary: string; error?: string }> {
    return fetchWithAuth('/portal/narrator/summary', {
      method: 'POST',
      body: JSON.stringify({ contextType, contextData }),
    });
  },
};

// =============================================================================
// CONSTITUTION / IMPACT ANALYSIS API
// =============================================================================

export interface PolicyImpactAnalysis {
  analysisId: string;
  normalizedChange: {
    action: string;
    resourceType: string;
    scope?: string;
    environment?: string;
    changeType: string;
    confidence: number;
  };
  riskLevel: string;
  confidenceScore: number;
  triggeredPolicies: { policyName: string; expectedOutcome: string; triggerLikelihood: number }[];
  affectedPrinciples: string[];
  affectedControls: { controlId: string; controlName: string; frameworkCode: string; impactType: string }[];
  graphContext: {
    relatedPolicies: number;
    relatedControls: number;
    relatedAssets: number;
    relatedExceptions: number;
  };
  estimatedImpact: {
    riskLevel: string;
    governanceScoreDelta: number;
    exposureDelta: number;
  };
  recommendation: {
    executiveSummary: string;
    technicalExplanation: string;
    saferAlternative?: string;
    exceptionPath?: string;
  };
}

export const constitutionApi = {
  async analyzeImpact(proposalText: string, environmentHint?: string, assetHint?: string): Promise<PolicyImpactAnalysis> {
    return fetchWithAuth('/portal/constitution/impact/analyze', {
      method: 'POST',
      body: JSON.stringify({ proposalText, environmentHint, assetHint }),
    });
  },
};

// =============================================================================
// AI GOVERNANCE API
// =============================================================================

/** Evidence reference attached to an AI decision — mirrors `EvidenceRefDto` (API). */
export interface AiEvidenceRef {
  refType: string;
  refId: string;
  refMetadata?: Record<string, unknown>;
}

/** AI decision ledger record — mirrors `AiDecisionDto` (`IAiGovernanceService.cs`). */
export interface AiDecision {
  id: string;
  tenantId: string;
  agentName: string;
  agentVersion: string;
  decisionType: string;
  subjectType: string;
  subjectId: string;
  outputSummary: string;
  confidenceScore: number;
  riskLevel: string;
  constitutionStatus: string;
  validationStatus: string;
  evidenceSupportStatus: string;
  finalStatus: string;
  evidenceRefs: AiEvidenceRef[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Paged decision list — mirrors `AiDecisionListResult` (API). */
export interface AiDecisionListResult {
  items: AiDecision[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AiConstitutionRule {
  id: string;
  code: string;
  severity: string;
  description: string;
  isEnabled: boolean;
  triggerCount: number;
  appliesTo: string[];
  createdAt: string;
}

export interface AiConstitutionViolation {
  id: string;
  ruleCode: string;
  decisionId: string;
  agentName: string;
  severity: string;
  description: string;
  detectedAt: string;
}

export interface AiGovernanceMetrics {
  totalDecisions: number;
  evidenceVerifiedRate: number;
  unsupportedRate: number;
  validationPassRate: number;
  constitutionViolations: number;
  reviewRequired: number;
}

export interface AiSimulation {
  id: string;
  description: string;
  source: string;
  status: string;
  blastRadius: number;
  falsePositiveRisk: number;
  createdAt: string;
}

export const aiGovernanceApi = {
  /** Paged decision list — `GET /api/portal/ai-governance/decisions`. */
  async getDecisions(params?: {
    status?: string;
    decisionType?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AiDecisionListResult> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.decisionType) qs.set('decisionType', params.decisionType);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    return fetchWithAuth(`/portal/ai-governance/decisions?${qs}`);
  },

  async getDecision(id: string): Promise<AiDecision> {
    return fetchWithAuth(`/portal/ai-governance/decisions/${id}`);
  },

  async getMetrics(): Promise<AiGovernanceMetrics> {
    return fetchWithAuth('/portal/ai-governance/metrics');
  },

  async getRules(): Promise<AiConstitutionRule[]> {
    return fetchWithAuth('/portal/ai-governance/constitution/rules');
  },

  async createRule(rule: Partial<AiConstitutionRule>): Promise<AiConstitutionRule> {
    return fetchWithAuth('/portal/ai-governance/constitution/rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    });
  },

  async updateRule(id: string, updates: Partial<AiConstitutionRule>): Promise<AiConstitutionRule> {
    return fetchWithAuth(`/portal/ai-governance/constitution/rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async getViolations(): Promise<AiConstitutionViolation[]> {
    return fetchWithAuth('/portal/ai-governance/constitution/violations');
  },

  async getSimulations(): Promise<AiSimulation[]> {
    return fetchWithAuth('/portal/ai-governance/simulations');
  },

  async createSimulation(sim: Partial<AiSimulation>): Promise<AiSimulation> {
    return fetchWithAuth('/portal/ai-governance/simulations', {
      method: 'POST',
      body: JSON.stringify(sim),
    });
  },

  async approveSimulation(id: string): Promise<void> {
    return fetchWithAuth(`/portal/ai-governance/simulations/${id}/approve`, { method: 'POST' });
  },

  async rejectSimulation(id: string): Promise<void> {
    return fetchWithAuth(`/portal/ai-governance/simulations/${id}/reject`, { method: 'POST' });
  },
};

// =============================================================================
// AUTOREPAIR API
// =============================================================================

export interface RepairJob {
  id: string;
  title: string;
  description: string;
  severity: string;
  repairMode: 'manual' | 'assisted' | 'auto';
  status: string;
  executionTarget: string;
  confidence: number;
  requiresApproval: boolean;
  violationId?: string;
  policyId?: string;
  controlId?: string;
  frameworkRefs?: string[];
  generatedFix?: object;
  executionResult?: object;
  verificationResult?: object;
  evidenceRecordId?: string;
  createdAt: string;
  updatedAt: string;
}

export const autorepairApi = {
  async getJobs(params?: { status?: string; mode?: string; severity?: string }): Promise<{ items: RepairJob[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.mode) qs.set('mode', params.mode);
    if (params?.severity) qs.set('severity', params.severity);
    return fetchWithAuth(`/portal/autorepair/jobs?${qs}`);
  },

  async getJob(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}`);
  },

  async createFromViolation(violationId: string): Promise<RepairJob> {
    return fetchWithAuth('/portal/autorepair/jobs/from-violation', {
      method: 'POST',
      body: JSON.stringify({ violationId }),
    });
  },

  async generateFix(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/generate`, { method: 'POST' });
  },

  async approveFix(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/approve`, { method: 'POST' });
  },

  async rejectFix(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/reject`, { method: 'POST' });
  },

  async executeFix(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/execute`, { method: 'POST' });
  },

  async verifyFix(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/verify`, { method: 'POST' });
  },

  async rollback(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/rollback`, { method: 'POST' });
  },

  async manualResolve(id: string): Promise<RepairJob> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/manual-resolve`, { method: 'POST' });
  },

  async submitFeedback(id: string, feedback: { rating: string; comment?: string }): Promise<void> {
    return fetchWithAuth(`/portal/autorepair/jobs/${id}/feedback`, {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  },
};

// =============================================================================
// NOTIFICATIONS API
// =============================================================================

export const notificationsApi = {
  /**
   * Get notifications
   */
  async getAll(unreadOnly = false): Promise<Notification[]> {
    const params = new URLSearchParams();
    if (unreadOnly) params.set('unreadOnly', 'true');
    return fetchWithAuth(`/portal/notifications?${params}`);
  },

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<void> {
    return fetchWithAuth(`/portal/notifications/${id}/read`, {
      method: 'POST',
    });
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    return fetchWithAuth('/portal/notifications/read-all', {
      method: 'POST',
    });
  },

  /**
   * Dismiss a notification
   */
  async dismiss(id: string): Promise<void> {
    return fetchWithAuth(`/portal/notifications/${id}/dismiss`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// DASHBOARD API
// =============================================================================

export const dashboardApi = {
  /**
   * Get security dashboard data
   */
  async getSecurityDashboard(): Promise<SecurityDashboard> {
    return fetchWithAuth('/portal/dashboard/security');
  },

  /**
   * Get enforcement dashboard data
   */
  async getComplianceDashboard(): Promise<ComplianceDashboard> {
    return fetchWithAuth('/portal/dashboard/compliance');
  },

  /**
   * Get enforcement outcomes (Allow/Warn/Block decision metrics with trends)
   */
  async getEnforcementOutcomes(periodDays = 30): Promise<EnforcementOutcomes> {
    return fetchWithAuth(`/portal/dashboard/enforcement-outcomes?periodDays=${periodDays}`);
  },

  /**
   * Get composite dashboard overview (all tiers in a single call)
   */
  async getDashboardOverview(): Promise<DashboardOverview> {
    return fetchWithAuth('/portal/dashboard/overview');
  },

  /**
   * Get unified score summary with data freshness indicators
   */
  async getScoreSummary(): Promise<ScoreSummary> {
    return fetchWithAuth('/portal/dashboard/score-summary');
  },
};

// =============================================================================
// SETTINGS API
// =============================================================================

export interface TenantSettingsData {
  // Branding
  logoUrl?: string;
  primaryColor?: string;
  customDomain?: string;

  // Authentication
  ssoProvider?: string;
  ssoMetadataUrl?: string;
  requireMfa: boolean;

  // Compliance
  enabledFrameworks: string[];
  requiredFrameworks: string[];
  enforcePoliciesOnPr: boolean;

  // MCP
  mcpRateLimitPerMinute: number;
  mcpMonthlyQuota: number;
  mcpEnabled: boolean;

  // Notifications
  slackWebhookUrl?: string;
  notificationEmail?: string;
  emailNotificationsEnabled: boolean;

  // Data Retention
  evidenceRetentionDays: number;
  auditLogRetentionDays: number;
}

export interface TenantSettingsResponse {
  settings: TenantSettingsData;
  subscription: 'Free' | 'Pro' | 'Enterprise';
  capabilities: {
    canConfigureSso: boolean;
    canConfigureCustomDomain: boolean;
    canConfigureBranding: boolean;
    maxApiRateLimit: number;
    maxMcpMonthlyQuota: number;
  };
}

export interface CurrentTenantResponse {
  referenceId: string;
  name: string;
  subscription: 'Free' | 'Pro' | 'Enterprise';
  isSubTenant: boolean;
  parentTenantId?: string;
  createdAt: string;
}

export const settingsApi = {
  /**
   * Get current tenant information
   */
  async getCurrentTenant(): Promise<CurrentTenantResponse> {
    return fetchWithAuth('/portal/tenants/current');
  },

  /**
   * Get tenant settings
   */
  async getTenantSettings(): Promise<TenantSettingsResponse> {
    return fetchWithAuth('/portal/tenants/current/settings');
  },

  /**
   * Update tenant settings
   */
  async updateTenantSettings(settings: Partial<TenantSettingsData>): Promise<CurrentTenantResponse> {
    return fetchWithAuth('/portal/tenants/current/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings }),
    });
  },
};

// =============================================================================
// ADMIN API
// =============================================================================

export const adminApi = {
  /**
   * Get integrations summary for admin dashboard
   */
  async getIntegrationsSummary(): Promise<IntegrationsSummary> {
    return fetchWithAuth('/portal/admin/integrations-summary');
  },

  /**
   * Get activity log entries
   */
  async getActivityLog(type?: ActivityType, limit = 50): Promise<ActivityLogEntry[]> {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('limit', limit.toString());
    return fetchWithAuth(`/portal/admin/activity?${params}`);
  },
};

// =============================================================================
// EVIDENCE API
// =============================================================================

export interface EvidenceSummary {
  from: string;
  to: string;
  totalEvaluations: number;
  byDecision: Record<string, number>;
  byFramework: Record<string, number>;
  chainIntegrity: boolean;
}

export interface EvidenceRecord {
  id: string;
  timestamp: string;
  type: string;
  decision: string;
  frameworks: string[];
  violationCount: number;
  hash: string;
  previousHash: string;
}

export interface ChainVerificationResult {
  isValid: boolean;
  from: string;
  to: string;
  evaluationsVerified: number;
  brokenLinks: Array<{
    evaluationId: string;
    expectedHash: string;
    actualHash: string;
    position: number;
  }>;
}

export interface ExportResult {
  format: string;
  fileName: string;
  contentType: string;
  data: string;
  recordCount: number;
  exportedAt: string;
}

export interface EvidenceFilter {
  from: Date;
  to: Date;
  decision?: string;
  framework?: string;
  limit?: number;
}

export interface ExportOptions {
  from: Date;
  to: Date;
  format: 'json' | 'csv' | 'sarif' | 'pdf';
  includeVerificationScript?: boolean;
}

export const evidenceApi = {
  /**
   * Get evidence summary for date range
   */
  async getSummary(from: Date, to: Date): Promise<EvidenceSummary> {
    const params = new URLSearchParams({
      from: from.toISOString(),
      to: to.toISOString(),
    });
    return fetchWithAuth(`/portal/evidence/summary?${params}`);
  },

  /**
   * Get evidence records
   */
  async getRecords(filter: EvidenceFilter): Promise<EvidenceRecord[]> {
    const params = new URLSearchParams({
      from: filter.from.toISOString(),
      to: filter.to.toISOString(),
    });
    if (filter.decision) params.set('decision', filter.decision);
    if (filter.framework) params.set('framework', filter.framework);
    if (filter.limit) params.set('limit', filter.limit.toString());
    return fetchWithAuth(`/portal/evidence/records?${params}`);
  },

  /**
   * Verify evidence chain integrity
   */
  async verifyChain(from: Date, to: Date): Promise<ChainVerificationResult> {
    return fetchWithAuth('/portal/evidence/verify', {
      method: 'POST',
      body: JSON.stringify({
        from: from.toISOString(),
        to: to.toISOString(),
      }),
    });
  },

  /**
   * Export evidence in specified format
   */
  async export(options: ExportOptions): Promise<ExportResult> {
    return fetchWithAuth('/portal/evidence/export', {
      method: 'POST',
      body: JSON.stringify({
        from: options.from.toISOString(),
        to: options.to.toISOString(),
        format: options.format,
        includeVerificationScript: options.includeVerificationScript ?? true,
      }),
    });
  },
};

// =============================================================================
// ENFORCEMENT API
// =============================================================================

export type ViolationAction = 'Allow' | 'Warn' | 'Block';
export type ControlSeverity = 'Info' | 'Low' | 'Medium' | 'High' | 'Critical';
export type PolicyStage = 'Shadow' | 'Canary' | 'Production';

export interface FrameworkOverride {
  frameworkId: number;
  frameworkCode: string;
  frameworkName: string;
  action: ViolationAction;
  stageOverride?: PolicyStage | null;
  isEnabled: boolean;
}

export interface EnforcementConfig {
  isEnabled: boolean;
  defaultAction: ViolationAction;
  minimumSeverity: ControlSeverity;
  strictMode: boolean;
  stage: PolicyStage;
  frameworkOverrides: FrameworkOverride[];
}

export interface UpdateEnforcementConfigRequest {
  isEnabled: boolean;
  defaultAction: ViolationAction;
  minimumSeverity: ControlSeverity;
  strictMode: boolean;
  stage: PolicyStage;
}

export interface UpdateFrameworkOverrideRequest {
  frameworkCode: string;
  action: ViolationAction;
  isEnabled: boolean;
}

export const enforcementApi = {
  /**
   * Get enforcement configuration
   */
  async getConfig(): Promise<EnforcementConfig> {
    return fetchWithAuth('/portal/mcp/configuration');
  },

  /**
   * Update global enforcement configuration
   */
  async updateConfig(request: UpdateEnforcementConfigRequest): Promise<EnforcementConfig> {
    return fetchWithAuth('/portal/mcp/configuration', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  /**
   * Update framework-specific enforcement override
   */
  async updateFrameworkOverride(request: UpdateFrameworkOverrideRequest): Promise<void> {
    return fetchWithAuth(`/portal/mcp/frameworks/${request.frameworkCode}/override`, {
      method: 'PUT',
      body: JSON.stringify({
        action: request.action,
        isEnabled: request.isEnabled,
      }),
    });
  },
};

// =============================================================================
// DIAGNOSTIC API
// =============================================================================

export interface BlindSpotListFilter {
  status?: string;
  severity?: string;
  type?: string;
}

export const diagnosticApi = {
  async getQualificationStatus(): Promise<QualificationStatus> {
    return fetchWithAuth('/diagnostic/qualification-status');
  },

  async getSummary(): Promise<DiagnosticSummary> {
    return fetchWithAuth('/diagnostic/summary');
  },

  async getRuntimeBlindSpotsSummary(): Promise<RuntimeBlindSpotsSummary> {
    return fetchWithAuth('/diagnostic/runtime-blind-spots/summary');
  },

  async getRuntimeBlindSpotsList(filter?: BlindSpotListFilter): Promise<RuntimeBlindSpot[]> {
    const params = new URLSearchParams();
    if (filter?.status) params.set('status', filter.status);
    if (filter?.severity) params.set('severity', filter.severity);
    if (filter?.type) params.set('type', filter.type);
    const query = params.toString();
    return fetchWithAuth(`/diagnostic/runtime-blind-spots/list${query ? `?${query}` : ''}`);
  },

  async recalculateBlindSpots(): Promise<RuntimeBlindSpotsSummary> {
    return fetchWithAuth('/diagnostic/runtime-blind-spots/recalculate', {
      method: 'POST',
    });
  },

  async generateReport(): Promise<DiagnosticReportResponse> {
    return fetchWithAuth('/diagnostic/generate-report', {
      method: 'POST',
    });
  },

  async getTrend(days = 30): Promise<DiagnosticTrend> {
    return fetchWithAuth(`/diagnostic/trend?days=${days}`);
  },

  async getSchedule(): Promise<DiagnosticSchedule> {
    return fetchWithAuth('/diagnostic/schedule');
  },

  async exportData(format: DiagnosticExportFormat): Promise<Blob> {
    const token = await getAccessToken();
    const tenantIdentifier = getTenantIdentifier();
    const response = await fetch(`${API_BASE_URL}/diagnostic/export?format=${format}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-Identifier': tenantIdentifier || '',
      },
    });
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    return response.blob();
  },
};

// =============================================================================
// FRAMEWORKS API (extended)
// =============================================================================

export const frameworksApi = {
  async getControls(frameworkCode: string): Promise<ControlListResult> {
    return fetchWithAuth<ControlListResult>(`/portal/mcp/controls/${frameworkCode}`);
  },

  async getControlDetail(frameworkCode: string, controlId: string): Promise<ControlDetail> {
    return fetchWithAuth(`/portal/mcp/frameworks/${frameworkCode}/controls/${controlId}`);
  },

  async getControlMappings(frameworkCode: string): Promise<ControlMapping[]> {
    return fetchWithAuth(`/portal/mcp/frameworks/${frameworkCode}/mappings`);
  },

  async getCoverage(sourceFramework: string, targetFramework: string): Promise<FrameworkCoverage> {
    return fetchWithAuth(`/portal/mcp/mappings/coverage?sourceFrameworkCode=${encodeURIComponent(sourceFramework)}&targetFrameworkCode=${encodeURIComponent(targetFramework)}`);
  },

  async toggleControl(frameworkCode: string, controlId: string, enabled: boolean): Promise<void> {
    return fetchWithAuth(`/portal/mcp/controls/${frameworkCode}/${controlId}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  },

  async bulkToggle(frameworkCode: string, controlIds: string[], enabled: boolean): Promise<void> {
    return fetchWithAuth(`/portal/mcp/controls/${frameworkCode}/bulk-toggle`, {
      method: 'POST',
      body: JSON.stringify({ controlIds, enabled }),
    });
  },

  async exportMatrix(frameworkCode: string): Promise<Blob> {
    const url = `${API_BASE_URL}/portal/mcp/frameworks/${frameworkCode}/export`;
    const token = await getAccessToken();
    const tenantId = getTenantIdentifier();
    const response = await fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-Id': tenantId }),
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  },

  async getEvaluations(frameworkCode: string): Promise<{ controlId: string; passed: boolean; evaluatedAt: string }[]> {
    return fetchWithAuth(`/portal/mcp/frameworks/${frameworkCode}/evaluations`);
  },

  async getRecentEvaluations(take = 50): Promise<EvaluationSummary[]> {
    return fetchWithAuth(`/portal/mcp/evaluations?take=${take}`);
  },
};

// =============================================================================
// MERGE GATE API
// =============================================================================

/** Raw shape returned by the API — mirrors `WebhookRegistrationDto` from `MergeGateController.cs`
 *  verbatim (`eventTypesJson` is a JSON-encoded string array, not yet parsed). */
interface WebhookRegistrationDto {
  id: number;
  referenceId: string;
  name: string;
  url: string;
  isActive: boolean;
  eventTypesJson: string;
  lastTriggeredAt?: string;
  totalDeliveries: number;
  failedDeliveries: number;
  createdAt: string;
  secret?: string;
}

/** Request body for `POST /portal/merge-gate/webhooks` — mirrors `CreateWebhookRegistrationRequest`.
 *  `secret` and `eventTypes` are optional: the API auto-generates a secret and defaults
 *  `eventTypes` to `["validation.completed"]` when omitted. */
interface RegisterWebhookRequest {
  name: string;
  url: string;
  secret?: string;
  eventTypes?: string[];
}

function mapWebhook(dto: WebhookRegistrationDto): WebhookRegistration {
  let eventTypes: string[] = [];
  try {
    const parsed: unknown = JSON.parse(dto.eventTypesJson);
    if (Array.isArray(parsed)) {
      eventTypes = parsed.filter((v): v is string => typeof v === 'string');
    }
  } catch {
    eventTypes = [];
  }

  return {
    id: dto.id,
    referenceId: dto.referenceId,
    name: dto.name,
    url: dto.url,
    isActive: dto.isActive,
    eventTypes,
    lastTriggeredAt: dto.lastTriggeredAt,
    totalDeliveries: dto.totalDeliveries,
    failedDeliveries: dto.failedDeliveries,
    createdAt: dto.createdAt,
    secret: dto.secret,
  };
}

export const mergeGateApi = {
  async getPolicy(repositoryId: string): Promise<MergeGatePolicy> {
    return fetchWithAuth(`/portal/merge-gate/policies/${repositoryId}`);
  },

  async getPolicies(): Promise<MergeGatePolicy[]> {
    return fetchWithAuth('/portal/merge-gate/policies');
  },

  async updatePolicy(repositoryId: string, request: UpdateMergeGatePolicyRequest): Promise<MergeGatePolicy> {
    return fetchWithAuth(`/portal/merge-gate/policies/${repositoryId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  async listWebhooks(): Promise<WebhookRegistration[]> {
    const dtos = await fetchWithAuth<WebhookRegistrationDto[]>('/portal/merge-gate/webhooks');
    return dtos.map(mapWebhook);
  },

  async registerWebhook(request: RegisterWebhookRequest): Promise<WebhookRegistration> {
    const dto = await fetchWithAuth<WebhookRegistrationDto>('/portal/merge-gate/webhooks', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return mapWebhook(dto);
  },

  async deleteWebhook(id: number): Promise<void> {
    return fetchWithAuth(`/portal/merge-gate/webhooks/${id}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// SUPPRESSION API
// =============================================================================

export const suppressionApi = {
  async getPending(): Promise<PendingSuppression[]> {
    return fetchWithAuth('/portal/findings/suppressions/pending');
  },

  async requestSuppression(findingId: string, reason: string, justification: string, expiresAt?: string): Promise<void> {
    return fetchWithAuth(`/portal/findings/${findingId}/suppress`, {
      method: 'POST',
      body: JSON.stringify({ reason, justification, expiresAt }),
    });
  },

  async approveSuppression(suppressionId: string, notes?: string): Promise<void> {
    return fetchWithAuth(`/portal/findings/suppressions/${suppressionId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  async rejectSuppression(suppressionId: string, notes?: string): Promise<void> {
    return fetchWithAuth(`/portal/findings/suppressions/${suppressionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  async getAudit(findingId?: string): Promise<SuppressionAuditEntry[]> {
    const params = new URLSearchParams();
    if (findingId) params.set('findingId', findingId);
    const query = params.toString();
    return fetchWithAuth(`/portal/findings/suppressions/audit${query ? `?${query}` : ''}`);
  },
};

// =============================================================================
// NOTIFICATION SETTINGS API
// =============================================================================

export const notificationSettingsApi = {
  async getPreferences(): Promise<NotificationPreference[]> {
    return fetchWithAuth('/portal/notifications/preferences');
  },

  async updatePreferences(preferences: NotificationPreference[]): Promise<void> {
    return fetchWithAuth('/portal/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  },

  async getChannels(): Promise<NotificationChannel[]> {
    return fetchWithAuth('/portal/notifications/channels');
  },

  async getDeliveries(limit = 50): Promise<NotificationDelivery[]> {
    return fetchWithAuth(`/portal/notifications/deliveries?limit=${limit}`);
  },
};

// =============================================================================
// BILLING API
// =============================================================================

export const billingApi = {
  async getCurrentUsage(): Promise<UsageSummary> {
    return fetchWithAuth('/portal/billing/usage/current');
  },

  async getUsageHistory(periodDays = 30): Promise<UsageHistory[]> {
    return fetchWithAuth(`/portal/billing/usage/history?periodType=daily&limit=${periodDays}`);
  },

  async getUsageByTool(): Promise<Record<string, number>> {
    return fetchWithAuth('/portal/billing/usage/by-tool');
  },

  async getInvoices(): Promise<Invoice[]> {
    return fetchWithAuth('/portal/billing/invoices');
  },

  async getInvoice(id: string): Promise<Invoice> {
    return fetchWithAuth(`/portal/billing/invoices/${id}`);
  },

  async getSubscription(): Promise<SubscriptionInfo> {
    return fetchWithAuth('/portal/billing/subscription');
  },

  async getRateLimits(): Promise<RateLimitStatus[]> {
    return fetchWithAuth('/portal/billing/rate-limits');
  },
};

// =============================================================================
// REPORTS API
// =============================================================================

export const reportsApi = {
  async getTypes(): Promise<ReportType[]> {
    return fetchWithAuth('/portal/reports/types');
  },

  async generate(request: GenerateReportRequest): Promise<Blob> {
    const url = `${API_BASE_URL}/portal/reports/generate`;
    const token = await getAccessToken();
    const tenantId = getTenantIdentifier();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-Id': tenantId }),
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  },
};

// =============================================================================
// ENTERPRISE KNOWLEDGE GRAPH (EKG) API
// =============================================================================

export const ekgApi = {
  async getStats(): Promise<import('../types').EkgStats> {
    return fetchWithAuth('/portal/ekg/stats');
  },

  async getNodes(nodeType?: string, skip = 0, take = 50): Promise<import('../types').EkgNodeList> {
    const params = new URLSearchParams();
    if (nodeType) params.set('nodeType', nodeType);
    params.set('skip', skip.toString());
    params.set('take', take.toString());
    return fetchWithAuth(`/portal/ekg/nodes?${params}`);
  },

  async getContext(nodeId: number, maxHops = 2): Promise<import('../types').EkgContext> {
    return fetchWithAuth(`/portal/ekg/context/${nodeId}?maxHops=${maxHops}`);
  },

  /**
   * Resolve EKG context by public GUID reference id — §12773: enables the cold
   * deep-link case (`?node={referenceId}` for a node not present on the currently
   * loaded page) to resolve cross-page instead of silently no-op'ing. 404s if the
   * reference id doesn't match any node.
   */
  async getContextByReferenceId(referenceId: string, maxHops = 2): Promise<import('../types').EkgContext> {
    return fetchWithAuth(`/portal/ekg/context/by-reference/${referenceId}?maxHops=${maxHops}`);
  },

  async getCoverage(requirementId: string): Promise<import('../types').EkgCoverage> {
    return fetchWithAuth(`/portal/ekg/coverage/${encodeURIComponent(requirementId)}`);
  },

  async syncEntity(request: {
    nodeType: string;
    externalId: string;
    label: string;
    properties?: string;
  }): Promise<import('../types').EkgNode> {
    return fetchWithAuth('/portal/ekg/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  },
};

// =============================================================================
// GOVERNANCE POSTURE (F040)
// =============================================================================

export const governanceApi = {
  async getPosture(timeframe = 30): Promise<import('../types').GovernancePosture> {
    return fetchWithAuth(`/portal/governance/posture?timeframe=${timeframe}`);
  },

  async getBoardPosture(timeframe = 30): Promise<import('../types').BoardPosture> {
    return fetchWithAuth(`/portal/governance/posture/board?timeframe=${timeframe}`);
  },

  async getCoverageSummary(): Promise<import('../types').RuntimeCoverageSummary> {
    return fetchWithAuth('/portal/governance/coverage/summary');
  },

  async getCoverageBreakdown(params?: {
    framework?: string;
    policyClass?: string;
    scopeType?: string;
    days?: number;
  }): Promise<import('../types').CoverageBreakdownItem[]> {
    const searchParams = new URLSearchParams();
    if (params?.framework) searchParams.set('framework', params.framework);
    if (params?.policyClass) searchParams.set('policyClass', params.policyClass);
    if (params?.scopeType) searchParams.set('scopeType', params.scopeType);
    if (params?.days) searchParams.set('days', params.days.toString());
    return fetchWithAuth(`/portal/governance/coverage/breakdown?${searchParams}`);
  },

  async getCoverageGaps(): Promise<import('../types').CoverageGaps> {
    return fetchWithAuth('/portal/governance/coverage/gaps');
  },

  async getCoverageTrend(days = 90): Promise<import('../types').CoverageTrendPoint[]> {
    return fetchWithAuth(`/portal/governance/coverage/trend?days=${days}`);
  },
};

// =============================================================================
// POLICY EXCEPTIONS (F041)
// =============================================================================

export const exceptionsApi = {
  async list(status?: string): Promise<import('../types').PolicyException[]> {
    const params = status ? `?status=${status}` : '';
    return fetchWithAuth(`/portal/exceptions${params}`);
  },

  async getPending(): Promise<import('../types').PolicyException[]> {
    return fetchWithAuth('/portal/exceptions/pending');
  },

  async getAnalytics(): Promise<import('../types').ExceptionAnalytics> {
    return fetchWithAuth('/portal/exceptions/analytics');
  },

  async requestException(request: import('../types').ExceptionRequestPayload): Promise<{ exceptionId: number; status: string }> {
    return fetchWithAuth('/portal/exceptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  },

  async approve(id: number): Promise<{ message: string }> {
    return fetchWithAuth(`/portal/exceptions/${id}/approve`, { method: 'PUT' });
  },

  async deny(id: number): Promise<{ message: string }> {
    return fetchWithAuth(`/portal/exceptions/${id}/deny`, { method: 'PUT' });
  },

  async revoke(id: number): Promise<{ message: string }> {
    return fetchWithAuth(`/portal/exceptions/${id}/revoke`, { method: 'PUT' });
  },

  async renew(id: number, ttlHours: number): Promise<{ message: string }> {
    return fetchWithAuth(`/portal/exceptions/${id}/renew`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ttlHours }),
    });
  },
};

// =============================================================================
// AUDIT ENGAGEMENTS (F042)
// =============================================================================

export const auditApi = {
  async listEngagements(): Promise<import('../types').AuditEngagement[]> {
    return fetchWithAuth('/portal/audit/engagements');
  },

  async getEngagement(id: number): Promise<import('../types').AuditEngagement> {
    return fetchWithAuth(`/portal/audit/engagements/${id}`);
  },

  async createEngagement(request: import('../types').CreateEngagementRequest): Promise<{ id: number; title: string; status: string }> {
    return fetchWithAuth('/portal/audit/engagements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  },

  async closeEngagement(id: number): Promise<{ message: string }> {
    return fetchWithAuth(`/portal/audit/engagements/${id}/close`, { method: 'PUT' });
  },

  async generateBundle(id: number): Promise<{ bundleId: number; fileName: string; contentHash: string; recordCount: number }> {
    return fetchWithAuth(`/portal/audit/engagements/${id}/bundle`, { method: 'POST' });
  },

  async listBundles(id: number): Promise<import('../types').EvidenceBundle[]> {
    return fetchWithAuth(`/portal/audit/engagements/${id}/bundles`);
  },
};

// =============================================================================
// DASHBOARD LOOP METRICS (F043)
// =============================================================================

export const loopMetricsApi = {
  async getLoopMetrics(): Promise<import('../types').LoopMetrics> {
    return fetchWithAuth('/portal/dashboard/loop-metrics');
  },
};

// =============================================================================
// PERSONA (F044)
// =============================================================================

export const personaApi = {
  async getPersona(): Promise<import('../types').UserPersona> {
    return fetchWithAuth('/portal/me/persona');
  },
};

// =============================================================================
// GLOBAL ASSISTANT (F045)
// =============================================================================

export const assistantApi = {
  async sendMessage(request: import('../types').AssistantMessageRequest): Promise<import('../types').AssistantResponse> {
    return fetchWithAuth('/portal/assistant/message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  },

  async getContext(page?: string, persona?: string): Promise<import('../types').AssistantContext> {
    const params = new URLSearchParams();
    if (page) params.set('page', page);
    if (persona) params.set('persona', persona);
    return fetchWithAuth(`/portal/assistant/context?${params}`);
  },

  async sendMessageStream(
    request: import('../types').AssistantMessageRequest,
    callbacks: {
      onToken: (text: string) => void;
      onDone: (meta: { inputTokens: number; outputTokens: number }) => void;
      onError: (error: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const url = `${API_BASE_URL}/portal/assistant/message/stream`;
    const token = await getAccessToken();
    const tenantId = getTenantIdentifier();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-Id': crypto.randomUUID(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok || !response.body) {
      callbacks.onError(`Request failed: ${response.status}`);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const eventType = line.slice(7).trim();
          const dataLine = lines[lines.indexOf(line) + 1];
          if (dataLine?.startsWith('data: ')) {
            try {
              const data = JSON.parse(dataLine.slice(6));
              if (eventType === 'text') callbacks.onToken(data.text);
              else if (eventType === 'done') callbacks.onDone(data);
              else if (eventType === 'error') callbacks.onError(data.error);
            } catch { /* ignore parse errors */ }
          }
        }
      }
    }
  },
};

// =============================================================================
// AGENT REGISTRY (F031) API
// =============================================================================
// The customer's AI agent estate governed as first-class assets. Binds to
// `LeftShield.API.Controllers.Portal.AgentsController` (`api/portal/agents`). Registration and
// update back the FS-F031-AGENT form-spec (Register Agent / Edit Agent forms).

export const agentsApi = {
  /**
   * Paged, filtered list of governed agents — server-side filters (skip/take clamped to
   * 1-200 by the API, matching the adaptive-grid `threshold` used on the Agent Registry page).
   */
  async getAll(filter: AgentListFilter = {}, skip = 0, take = 50): Promise<AgentPagedResult> {
    const params = new URLSearchParams();
    if (filter.environment) params.set('environment', filter.environment);
    if (filter.approvalState) params.set('approvalState', filter.approvalState);
    if (filter.minRiskTier) params.set('minRiskTier', filter.minRiskTier);
    if (filter.status) params.set('status', filter.status);
    if (filter.mcpEnabled !== undefined) params.set('mcpEnabled', String(filter.mcpEnabled));
    if (filter.ownerTeam) params.set('ownerTeam', filter.ownerTeam);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/agents?${params}`);
  },

  /** Full agent detail — entry, versions, bindings, approvals, and the F030 graph-node key. */
  async getById(id: number): Promise<AgentDetail> {
    return fetchWithAuth(`/portal/agents/${id}`);
  },

  /** Register a new governed agent — computes its risk tier, enforces the production-placement
   *  guard, and projects it onto the F030 Governance Graph. A-01 (FS-F031-AGENT). */
  async register(input: AgentRegistrationInput): Promise<Agent> {
    return fetchWithAuth('/portal/agents', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** Partial update (a material change opens a new version + re-pends approval). */
  async update(id: number, patch: UpdateAgentRequest): Promise<Agent> {
    return fetchWithAuth(`/portal/agents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  /** Recomputes the risk tier, re-projects posture onto the graph node, returns the
   *  risk + production-guard verdict. */
  async evaluate(id: number): Promise<AgentRisk> {
    return fetchWithAuth(`/portal/agents/${id}/evaluate`, { method: 'POST' });
  },

  /** Shadow-AI sweep — agents observed in connector telemetry but absent from the registry.
   *  `detectionAvailable: false` means no telemetry source is connected. */
  async getUngoverned(): Promise<ShadowAiSweep> {
    return fetchWithAuth('/portal/agents/ungoverned');
  },
};

// =============================================================================
// MCP GOVERNANCE (F032) API
// =============================================================================
// Control-plane governance for MCP servers and the tools they expose. Binds to
// `LeftShield.API.Controllers.Portal.McpGovernanceController` (`api/portal/mcp-governance`).

export const mcpGovernanceApi = {
  /**
   * Paged, filtered list of registered MCP servers — server-side filters (skip/take clamped by
   * the API, matching the adaptive-grid `threshold` used on the MCP Governance page).
   */
  async getServers(filter: McpServerListFilter = {}, skip = 0, take = 50): Promise<McpServerPagedResult> {
    const params = new URLSearchParams();
    if (filter.approvalState) params.set('approvalState', filter.approvalState);
    if (filter.minRiskTier) params.set('minRiskTier', filter.minRiskTier);
    if (filter.environment) params.set('environment', filter.environment);
    if (filter.status) params.set('status', filter.status);
    if (filter.ownerTeam) params.set('ownerTeam', filter.ownerTeam);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/mcp-governance/servers?${params}`);
  },

  /** Full server detail — the server plus every tool it exposes. */
  async getServer(id: number): Promise<McpServerDetail> {
    return fetchWithAuth(`/portal/mcp-governance/servers/${id}`);
  },

  /** Register a new MCP server for governance — computes its risk tier from its declared access
   *  footprint and opens an approval workflow when warranted. */
  async registerServer(input: McpServerRegistrationInput): Promise<McpServer> {
    return fetchWithAuth('/portal/mcp-governance/servers', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** Approve a pending MCP server. */
  async approveServer(id: number): Promise<McpServer> {
    return fetchWithAuth(`/portal/mcp-governance/servers/${id}/approve`, { method: 'POST' });
  },

  /** Reject a pending MCP server with a reason. */
  async rejectServer(id: number, reason: string): Promise<McpServer> {
    return fetchWithAuth(`/portal/mcp-governance/servers/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /** Paged, filtered list of tools across registered servers. */
  async getTools(filter: McpToolListFilter = {}, skip = 0, take = 50): Promise<McpToolPagedResult> {
    const params = new URLSearchParams();
    if (filter.serverId !== undefined) params.set('serverId', String(filter.serverId));
    if (filter.minRiskTier) params.set('minRiskTier', filter.minRiskTier);
    if (filter.requiresApproval !== undefined) params.set('requiresApproval', String(filter.requiresApproval));
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/mcp-governance/tools?${params}`);
  },

  /** Register a new tool exposed by an already-registered MCP server. */
  async registerTool(input: McpToolRegistrationInput): Promise<McpTool> {
    return fetchWithAuth('/portal/mcp-governance/tools', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** Recomputes a tool's risk tier. */
  async assessTool(id: number): Promise<McpRiskAssessment> {
    return fetchWithAuth(`/portal/mcp-governance/tools/${id}/assess`, { method: 'POST' });
  },

  /** Approves a pending MCP tool. */
  async approveTool(id: number): Promise<McpTool> {
    return fetchWithAuth(`/portal/mcp-governance/tools/${id}/approve`, { method: 'POST' });
  },

  /** MCP servers/tools observed in traffic but unregistered or unapproved — the MCP-surface
   *  analogue of the Agent Registry's shadow-AI sweep. */
  async getUnapprovedUsage(): Promise<UnapprovedMcpUsage[]> {
    return fetchWithAuth('/portal/mcp-governance/unapproved-usage');
  },
};

// =============================================================================
// AUTONOMOUS GOVERNANCE RESPONSE (F039) API
// =============================================================================
// Policy CRUD, the Action Log, the Rule-4 proposal queue, and approve/reject/revert. Binds to
// `LeftShield.API.Controllers.Portal.AutonomousGovernanceController` (`api/portal/autonomous-governance`).
// F039 does NOT expose a public "enforce" endpoint — enforcement happens only through F034 REGE.

export const autonomousGovernanceApi = {
  /** Paged, current response policies. */
  async getPolicies(skip = 0, take = 50): Promise<ResponsePolicyPagedResult> {
    const params = new URLSearchParams();
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/autonomous-governance/policies?${params}`);
  },

  /** Creates (or versions) a response policy. */
  async createPolicy(input: CreateResponsePolicyInput): Promise<ResponsePolicy> {
    return fetchWithAuth('/portal/autonomous-governance/policies', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** Enables or disables a current response policy. */
  async setPolicyEnabled(externalId: string, enabled: boolean): Promise<ResponsePolicy> {
    return fetchWithAuth(`/portal/autonomous-governance/policies/${externalId}/enabled`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  },

  /** Paged, optionally filtered response history (the Action Log). */
  async getActions(
    filter: { state?: ResponseActionStatus; action?: ResponseActionType; subject?: string } = {},
    skip = 0,
    take = 50
  ): Promise<ResponseActionPagedResult> {
    const params = new URLSearchParams();
    if (filter.state) params.set('state', filter.state);
    if (filter.action) params.set('action', filter.action);
    if (filter.subject) params.set('subject', filter.subject);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/autonomous-governance/actions?${params}`);
  },

  /** A single response — links to the REGE decision + F037 lineage entry. */
  async getAction(responseId: string): Promise<ResponseAction> {
    return fetchWithAuth(`/portal/autonomous-governance/actions/${responseId}`);
  },

  /** Pending approval requests — the Rule-4 queue. */
  async getProposals(skip = 0, take = 50): Promise<ResponseProposalPagedResult> {
    const params = new URLSearchParams();
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/autonomous-governance/proposals?${params}`);
  },

  /** Approves a proposed response — applies it via REGE with Approver authority. No `principalId` in the
   *  body — the server reads the approver from the auth token. */
  async approveProposal(approvalRequestId: string, note?: string): Promise<ResponseAction> {
    return fetchWithAuth(`/portal/autonomous-governance/proposals/${approvalRequestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },

  /** Rejects a proposed response — moves it to Suppressed, zero REGE calls. No `principalId` in the body —
   *  the server reads the rejector from the auth token. */
  async rejectProposal(approvalRequestId: string, note?: string): Promise<ResponseAction> {
    return fetchWithAuth(`/portal/autonomous-governance/proposals/${approvalRequestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },

  /** Reverts a previously-applied reversible response via a compensating REGE directive. */
  async revertAction(responseId: string, reason: string): Promise<ResponseAction> {
    return fetchWithAuth(`/portal/autonomous-governance/actions/${responseId}/revert`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// =============================================================================
// RUNTIME GOVERNANCE ENGINE / REGE (F034) API
// =============================================================================
// Read-only decision-history surface. Binds to `LeftShield.API.Controllers.Portal.RuntimeGovernanceController`
// (`api/portal/runtime-governance-engine`). The `evaluate` endpoint is REGE's own hot-path write — not a
// portal-UI surface — so it is intentionally not exposed here.

export const runtimeGovernanceApi = {
  /** Paged, optionally verdict-filtered decision history. */
  async getDecisions(verdict?: RegeVerdict, skip = 0, take = 50): Promise<ExecutionDecisionPagedResult> {
    const params = new URLSearchParams();
    if (verdict) params.set('verdict', verdict);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/runtime-governance-engine/decisions?${params}`);
  },

  /** A single recorded decision by its stable DecisionId. */
  async getDecision(decisionId: string): Promise<ExecutionDecision> {
    return fetchWithAuth(`/portal/runtime-governance-engine/decisions/${decisionId}`);
  },
};

// =============================================================================
// TRUST / EXECUTION-READINESS ENGINE (F036) API
// =============================================================================
// Cached-read plus compute-on-demand for the Identity Trust score. Binds to
// `LeftShield.API.Controllers.Portal.TrustController` (`api/portal/trust`).

export const trustApi = {
  /** Reads the last-cached Identity Trust profile for an identity. */
  async getProfile(identityExternalId: string): Promise<TrustScore> {
    return fetchWithAuth(`/portal/trust/identities/${identityExternalId}`);
  },

  /** Computes (or recomputes) and caches the Identity Trust profile for an identity. */
  async recompute(identityExternalId: string): Promise<TrustScore> {
    return fetchWithAuth(`/portal/trust/identities/${identityExternalId}/recompute`, { method: 'POST' });
  },
};

// =============================================================================
// GOVERNANCE DRIFT DETECTION (F038) API
// =============================================================================
// Binds to `LeftShield.API.Controllers.Portal.GovernanceDriftController` (`api/portal/governance-drift`).

export const governanceDriftApi = {
  /** Paged, optionally filtered drift-signal history. */
  async getSignals(
    filter: { category?: DriftCategory; status?: DriftStatus } = {},
    skip = 0,
    take = 50
  ): Promise<DriftSignalPagedResult> {
    const params = new URLSearchParams();
    if (filter.category) params.set('category', filter.category);
    if (filter.status) params.set('status', filter.status);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/governance-drift/signals?${params}`);
  },

  /** A single identity's aggregated drift state (the same reduction REGE's `IDriftSignalProvider`
   *  applies) plus its open signals. */
  async getIdentityState(externalId: string): Promise<DriftIdentityState> {
    return fetchWithAuth(`/portal/governance-drift/identities/${externalId}/state`);
  },

  /** Runs all registered drift detectors and reconciles open signals (raise new / update existing /
   *  auto-resolve stopped drift). */
  async recompute(): Promise<DriftRecomputeSummary> {
    return fetchWithAuth('/portal/governance-drift/recompute', { method: 'POST' });
  },

  /** Cuts a new approved permission-edge baseline for the tenant. */
  async rebaseline(reason?: string): Promise<DriftBaseline> {
    return fetchWithAuth('/portal/governance-drift/rebaseline', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// =============================================================================
// EXECUTION SAFETY SIMULATOR (F035) API
// =============================================================================
// Binds to `LeftShield.API.Controllers.Portal.ExecutionSimulatorController` (`api/portal/execution-simulator`).

export const executionSimulatorApi = {
  /** Simulates one hypothetical execution request end-to-end — no F037 ledger write, no real execution
   *  decision. */
  async simulate(input: SimulateExecutionInput): Promise<SimulationResult> {
    return fetchWithAuth('/portal/execution-simulator/simulate', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** A single recorded simulation run by its stable SimulationId. */
  async getRun(simulationId: string): Promise<SimulationResult> {
    return fetchWithAuth(`/portal/execution-simulator/runs/${simulationId}`);
  },

  /** Paged simulation-run history. */
  async getRuns(skip = 0, take = 50): Promise<SimulationRunPagedResult> {
    const params = new URLSearchParams();
    params.set('skip', String(skip));
    params.set('take', String(take));
    return fetchWithAuth(`/portal/execution-simulator/runs?${params}`);
  },
};

// =============================================================================
// F044 VERDICT, VOICE & SCORE ARCHITECTURE — GOVERNANCE SCORE API
// =============================================================================
// Binds to `LeftShield.API.Controllers.Portal.GovernanceScoreController` (`api/portal/governance-score`).
// The ONE composite Governance Score — computed once by `GovernanceScoreComposer`, read everywhere else.

export const governanceScoreApi = {
  /** The ONE composite for the current tenant. `composite` is undefined when `isScoreable` is false —
   *  render `<EmptyVerdict>`, never a "0" score. */
  async getScore(): Promise<GovernanceScore> {
    return fetchWithAuth('/portal/governance-score');
  },

  /** The single decomposition (dimensions + weights) — the ONE page every other surface links to. */
  async getDecomposition(): Promise<ScoreDecomposition> {
    return fetchWithAuth('/portal/governance-score/decomposition');
  },

  /** Recomputes + snapshots the composite (also runs on a schedule / domain events, server-side). */
  async recompute(): Promise<GovernanceScore> {
    return fetchWithAuth('/portal/governance-score/recompute', { method: 'POST' });
  },

  /** The effective weight profile (tenant override, or the platform default). */
  async getWeightProfile(): Promise<WeightProfile> {
    return fetchWithAuth('/portal/governance-score/weight-profile');
  },

  /** Overrides the tenant's dimension weights. Server validates the weights sum to 1.0 and audits the change. */
  async updateWeightProfile(request: UpdateWeightProfileRequest): Promise<WeightProfile> {
    return fetchWithAuth('/portal/governance-score/weight-profile', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },
};

// =============================================================================
// F044 — GLOSSARY API
// =============================================================================
// Binds to `LeftShield.API.Controllers.Portal.GlossaryController` (`api/portal/glossary`). The canonical
// noun/verdict/colour-rule registry — source of truth is `tools/glossary.json`; served read-only.

export const glossaryApi = {
  async getGlossary(): Promise<Glossary> {
    return fetchWithAuth('/portal/glossary');
  },
};
